import { useEffect, useRef } from "react";

interface UseProgrammaticIntakeQueueOptions {
    intakeFiles?: File[];
    onIntakeConsumed?: () => void;
    onConsumeFile: (file: File) => void;
}

/**
 * Consumes one programmatic intake file at a time, idempotently.
 * Guards against duplicate effect executions before the parent queue head
 * has been shifted out (StrictMode/double-render timing).
 */
export function useProgrammaticIntakeQueue({
    intakeFiles,
    onIntakeConsumed,
    onConsumeFile
}: UseProgrammaticIntakeQueueOptions): void {
    const lastConsumedHeadRef = useRef<File | null>(null);

    useEffect(() => {
        if (!intakeFiles?.length) {
            lastConsumedHeadRef.current = null;
            return;
        }

        const head = intakeFiles[0];
        if (head === lastConsumedHeadRef.current) return;

        lastConsumedHeadRef.current = head;

        onConsumeFile(head);
        onIntakeConsumed?.();
    }, [intakeFiles, onConsumeFile, onIntakeConsumed]);
}
